'use client';
import type Keycloak from 'keycloak-js';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import {
  getKeycloakSync,
  getToken,
  hasKeycloakInitStarted,
  initKeycloak,
  isKeycloakInitialized,
} from '@/configs/keycloak.config';
import { authInterceptorWithGetter } from '@/services/auth-interceptor';
import { runtimeHttpClient } from '@/services/services.setup';
import { scheduleIdleCallback } from '@/utils/schedule-idle-callback';

type KeycloakContext = {
  keycloak: Keycloak | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
};

const KeycloakCtx = createContext<KeycloakContext>({
  keycloak: null,
  isInitialized: false,
  isAuthenticated: false,
});

export type User = {
  name: string;
  email: string;
};

export const KeycloakProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(() => isKeycloakInitialized());

  useEffect(() => {
    if (isInitialized) {
      // Warms the token cache; requests attach their own via the interceptor,
      // so a failure here is not worth surfacing to the user.
      getToken().catch(() => {});
    }
  }, [isInitialized]);

  useEffect(() => {
    let cancelled = false;
    const interceptorId = authInterceptorWithGetter(runtimeHttpClient, getToken);

    const markReady = () => {
      if (!cancelled) setIsInitialized(true);
    };

    const startInit = () => {
      initKeycloak()
        .catch((error: unknown) => {
          // Mark ready either way (see `.finally`): a failed SSO check means we
          // could not confirm a session, which the UI should render as "logged
          // out" — leaving `isInitialized` false strands consumers on their
          // loading state (the toolbar avatar sits on a skeleton forever).
          console.error('[keycloak] init failed — continuing as unauthenticated:', error);
        })
        .finally(markReady);
    };

    if (isKeycloakInitialized()) {
      markReady();
    } else if (hasKeycloakInitStarted()) {
      startInit();
    } else {
      scheduleIdleCallback(startInit, { timeout: 2000 });
    }

    return () => {
      cancelled = true;
      runtimeHttpClient.interceptors.request.eject(interceptorId);
    };
  }, []);

  const keycloak = getKeycloakSync() ?? null;
  const isAuthenticated = isInitialized && Boolean(keycloak?.authenticated);

  return (
    <KeycloakCtx.Provider value={{ keycloak, isInitialized, isAuthenticated }}>
      {children}
    </KeycloakCtx.Provider>
  );
};

export const useKeycloak = () => useContext(KeycloakCtx);
